/**
 * The cross-window half of a tab host: moving tabs to and from other WeaveIn
 * windows (menu "Move to ▸ …", dragging a tab onto another window's strip, and
 * tearing a tab off into a new window). Used by both hosts — the main window
 * (useMainWindowTabs) and a tabbed pop-out (AppTabsHost). The protocol and
 * its guarantees are described in lib/tabMove.ts.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/use-toast';
import { HOST_HEARTBEAT_MS, appTabKey, parseAppTabKey, writeHostEntry, type AppTabSpec } from '@/lib/popout';
import {
  DRAG_NONCE_TTL_MS,
  MOVE_ACK_TIMEOUT_MS,
  PendingMoves,
  SeenNonces,
  listMoveTargets,
  newNonce,
  sendTabMoveMessage,
  subscribeTabMoves,
  type MoveTarget,
  type TabMoveMessage,
} from '@/lib/tabMove';
import type { TabDragPayload } from '@/lib/tabStrip';

/** What a host did with a tab another window gave it. */
export type MoveInResult = 'ok' | 'full' | 'invalid';

export interface UseTabMoveOptions {
  /** This window's id (lib/tabMove.ts). */
  windowId: string;
  enabled: boolean;
  isDesktop: boolean;
  /** Take a tab from another window, at `index` in the strip (undefined = at the end). */
  onMoveIn: (spec: AppTabSpec, index: number | undefined, title: string | undefined) => MoveInResult;
  /** One of our tabs now lives in another window: let it go. */
  onMovedOut: (uid: string) => void;
}

export interface TabMoveApi {
  windowId: string;
  listTargets: () => Promise<MoveTarget[]>;
  /** Ask another window to take this tab; it is removed here only once that window confirms. */
  moveToWindow: (uid: string, spec: AppTabSpec, title: string | undefined, targetId: string) => Promise<boolean>;
  /** A drag of one of our tabs is starting: the payload it carries. */
  startDrag: (uid: string, spec: AppTabSpec, title: string | undefined) => TabDragPayload;
  /**
   * A drag ended with no strip taking it. True (once) if the tab is still ours
   * to tear off into a new window; false if another window already took it.
   */
  claimDragOut: (nonce: string) => boolean;
  /** Another window's tab was dropped on our strip at `index`. 'own' = it is one of ours (a reorder). */
  acceptDrop: (payload: TabDragPayload, index: number) => MoveInResult | 'own';
}

export function useTabMove({ windowId, enabled, isDesktop, onMoveIn, onMovedOut }: UseTabMoveOptions): TabMoveApi {
  const { t } = useTranslation();
  const pending = useRef(new PendingMoves());
  const seen = useRef(new SeenNonces());
  const handlers = useRef({ onMoveIn, onMovedOut });
  handlers.current = { onMoveIn, onMovedOut };

  const reply = useCallback(
    (to: string, nonce: string, result: MoveInResult) => {
      const msg: TabMoveMessage =
        result === 'ok'
          ? { type: 'weavein:tab-moved', target: to, source: windowId, nonce }
          : { type: 'weavein:tab-move-rejected', target: to, source: windowId, nonce, reason: result };
      void sendTabMoveMessage(msg, { isDesktop });
    },
    [windowId, isDesktop],
  );

  /** Take a tab in — once per nonce — and tell the source how it went. */
  const takeIn = useCallback(
    (source: string, nonce: string, tabKey: string, index: number | undefined, title: string | undefined): MoveInResult => {
      if (!seen.current.claim(nonce)) return 'invalid';
      const spec = parseAppTabKey(tabKey);
      const result: MoveInResult = spec ? handlers.current.onMoveIn(spec, index, title) : 'invalid';
      reply(source, nonce, result);
      return result;
    },
    [reply],
  );

  useEffect(() => {
    if (!enabled) return;
    return subscribeTabMoves(
      windowId,
      (msg) => {
        if (msg.type === 'weavein:tab-move') {
          if (takeIn(msg.source, msg.nonce, msg.tabKey, msg.index, msg.title) === 'ok' && !isDesktop) {
            // Best effort: browsers often ignore focus() from a background window.
            try {
              window.focus();
            } catch {
              /* ignore */
            }
          }
          return;
        }
        const uid = pending.current.take(msg.nonce);
        if (!uid) return; // not ours, already settled, or stale
        if (msg.type === 'weavein:tab-moved') handlers.current.onMovedOut(uid);
        else
          toast({
            variant: 'info',
            title:
              msg.reason === 'full'
                ? t('tabStrip.target_full', 'That window has no room for another tab. Close a tab there first.')
                : t('tabStrip.move_failed', 'The tab could not be moved. It is still here.'),
          });
      },
      { isDesktop },
    );
  }, [enabled, windowId, isDesktop, takeIn, t]);

  const listTargets = useCallback(() => listMoveTargets(windowId, { isDesktop }), [windowId, isDesktop]);

  const moveToWindow = useCallback(
    async (uid: string, spec: AppTabSpec, title: string | undefined, targetId: string) => {
      const nonce = newNonce();
      pending.current.add(nonce, uid, MOVE_ACK_TIMEOUT_MS);
      const msg: TabMoveMessage = { type: 'weavein:tab-move', target: targetId, source: windowId, nonce, tabKey: appTabKey(spec) };
      if (title) msg.title = title.slice(0, 120);
      const sent = await sendTabMoveMessage(msg, { isDesktop, focusTarget: true });
      const failed = () =>
        toast({ variant: 'error', title: t('tabStrip.move_failed', 'The tab could not be moved. It is still here.') });
      if (!sent) {
        pending.current.take(nonce);
        failed();
        return false;
      }
      window.setTimeout(() => {
        // No answer (the window closed, or is not listening): the tab stays here.
        // (take() at time 0 ignores the expiry: we only ask "still unanswered?")
        if (pending.current.take(nonce, 0) !== null) failed();
      }, MOVE_ACK_TIMEOUT_MS + 100);
      return true;
    },
    [windowId, isDesktop, t],
  );

  const startDrag = useCallback(
    (uid: string, spec: AppTabSpec, title: string | undefined): TabDragPayload => {
      const nonce = newNonce();
      pending.current.add(nonce, uid, DRAG_NONCE_TTL_MS);
      const payload: TabDragPayload = { v: 1, tabKey: appTabKey(spec), sourceWindowId: windowId, nonce };
      if (title) payload.title = title.slice(0, 120);
      return payload;
    },
    [windowId],
  );

  const claimDragOut = useCallback((nonce: string) => pending.current.take(nonce) !== null, []);

  const acceptDrop = useCallback(
    (payload: TabDragPayload, index: number): MoveInResult | 'own' => {
      if (payload.sourceWindowId === windowId) return 'own';
      return takeIn(payload.sourceWindowId, payload.nonce, payload.tabKey, index, payload.title);
    },
    [windowId, takeIn],
  );

  return { windowId, listTargets, moveToWindow, startDrag, claimDragOut, acceptDrop };
}

/**
 * Web: keep this window in the shared host registry (lib/popout.ts) so other
 * windows can list it under "Move to ▸" (and, for a tabbed pop-out, find it
 * for "Add to tab view"). Removed when the window goes away.
 */
export function useHostAnnouncement(id: string, opts: { enabled: boolean; kind: 'main' | 'tabs'; title: string }): void {
  const titleRef = useRef(opts.title);
  titleRef.current = opts.title;
  const beatRef = useRef<() => void>(() => {});
  const { enabled, kind } = opts;

  useEffect(() => {
    if (!enabled) return;
    let focusedAt = document.hasFocus() ? Date.now() : 0;
    const beat = () => {
      const now = Date.now();
      if (document.hasFocus()) focusedAt = now;
      writeHostEntry(id, { focusedAt, seenAt: now, kind, ...(titleRef.current ? { title: titleRef.current.slice(0, 120) } : {}) });
    };
    beatRef.current = beat;
    beat();
    const timer = window.setInterval(beat, HOST_HEARTBEAT_MS);
    const onFocus = () => {
      focusedAt = Date.now();
      beat();
    };
    const onHide = () => writeHostEntry(id, null);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', onHide);
    return () => {
      beatRef.current = () => {};
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
  }, [id, enabled, kind]);

  // A new title shows up in other windows' menus straight away.
  useEffect(() => {
    beatRef.current();
  }, [opts.title]);
}
